import { gql } from "lib/apolloClient";

import {
  commentNodeFragment,
  totalCommentNodeFragment,
  notificationFragment,
  staffFragment,
  staffPeriodListFragment,
  organizationFragment,
  organizationPypElementSetFragment,
  plannerTemplateFragment,
  organizationGradesFragment,
  plannerTemplateHelpTextFragment,
  organizationCoursesFragment,
  designationFragment,
  pypElementFragment,
  attachmentFragment,
  userIdentityDetailFragment,
  studentFragment,
  notificationListNodeFragment,
  organizationAttendanceOptionFragment,
  plannerElementSetFragment,
  thirdPartyAccountFragment,
  curriculumProgramFragment,
  parentFragment,
  childFragment,
  criteriaSetFragment,
  pypElementRatingFragment,
  plannerElementSetWithDepthLabelFragment,
  staffUnitPlanFragment,
  gradeFragment,
  academicYearFragment,
  nodeEditorFragment,
  plannerElementNodeFragment,
  userFragment,
  yearGroupFragments,
  pageInfoFragment,
  identityUserFragment,
  genericPlannerElementSetFragment,
  pypPlannerElementSetFragment,
  aiFragment,
  userDetailsHashFragment,
  genericTagFragment,
} from "./CommonFragments";

import { rubricFragment } from "Rubric/modules/RubricFragments";
import { checklistFragment } from "Checklist/modules/ChecklistFragments";
import { singlePointRubricFragment } from "SinglePointRubric/modules/SinglePointRubricFragments";
import { assessmentToolFragment } from "modules/AssessmentTool/AssessmentToolFragment";
import { AIMessageFragments } from "AppComponents/GPTPlayground/modules/GPTPlaygroundFragments";
import { standardRubricFragment } from "AppComponents/StandardRubric/modules/StandardRubricFragments";
import { gradeScaleToolFragment } from "AppComponents/AssessmentGradeScaleTool/modules/AssessmentGradeScaleToolFragments";

export const getAttachmentQuery = gql`
  query getAttachment($id: ID!) {
    node(id: $id, type: ATTACHMENT) {
      id
      ...attachmentItem
    }
  }
  ${attachmentFragment.attachment}
`;

export const getMultiAttachmentsQuery = gql`
  query getMultiAttachments($ids: [ID!]) {
    multiNode(ids: $ids, type: ATTACHMENT) {
      ... on Attachment {
        id
        ...attachmentItem
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const getOrganizationStudentListQuery = gql`
  query getStudentList(
    $organizationId: ID!
    $searchText: String
    $grades: [ID!]
    $courses: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        students(
          filters: {
            searchText: $searchText
            grades: $grades
            courses: $courses
          }
        ) {
          edges {
            node {
              grades
              ...studentItem
            }
          }
        }
      }
    }
  }
  ${studentFragment.basicStudentDetails}
`;

export const getGlobalConstantsQuery = gql`
  query getGlobalConstantsQuery {
    globalConstants {
      designations {
        ...designationItem
      }
    }
  }
  ${designationFragment.designation}
`;
export const getOrganizationCoursesQuery = gql`
  query getOrganizationCoursesQuery(
    $organizationId: ID!
    $filters: CourseFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationCourseItem
      }
    }
  }
  ${organizationCoursesFragment.organizationCourses}
`;

export const getOrganizationBasicCoursesQuery = gql`
  query getOrganizationCoursesQuery(
    $organizationId: ID!
    $filters: CourseFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationCourseItem
      }
    }
  }
  ${organizationCoursesFragment.organizationBasicCourses}
`;

export const getOrganizationGradeBasicDetailsQuery = gql`
  query getOrganizationGrades(
    $organizationId: ID!
    $filters: GradeFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationGradesItem
      }
    }
  }
  ${organizationGradesFragment.organizationGrades}
`;

export const getOrganizationAcademicYearsQuery = gql`
  query getOrganizationAcademicYears($id: ID!, $filters: TermFilters) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        academicYears {
          ...academicYearBasicDetailsItem
          terms(filters: $filters) {
            ...termBasicDetailsItem
          }
        }
      }
    }
  }
  ${academicYearFragment.academicYearBasicDetails}
  ${academicYearFragment.termBasicDetails}
`;

export const getAcademicYearTermsQuery = gql`
  query getAcademicYearTerms($academicYearId: ID!, $filters: TermFilters) {
    node(id: $academicYearId, type: ACADEMIC_YEAR) {
      id
      ... on AcademicYear {
        id
        terms(filters: $filters) {
          id
          label
          startDate
          endDate
          type
        }
      }
    }
  }
`;

export const getPlatformUserDetailsQuery = gql`
  query getPlatformUserDetails($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        ...staffItem
        ...staffCoachMarksItem
        firstLoggedInAt
        type
        userDetailsHash {
          ...userDetailsHashItem
        }
        phoneNumber
        designation {
          id
          title
        }
        academicYears(filters: { archivalState: ACTIVE }) {
          ...academicYearItem
        }
        jwt
        isImpersonated
        linkedIdentityUsersV2 {
          totalCount
        }
        linkedIdentityV2 {
          id
        }
        organization {
          id
          name
          tier {
            plan
            isFree
          }
          academicYears {
            id
            isCurrentAcademicYear
          }
          curriculumPrograms {
            ...curriculumProgramBasicDetailsItem #TODO: Create copy of this query for ChildrenPage and revert changes
            ...subscriptionPlanDetailsItem
          }
        }
      }
      ... on Student {
        ...studentItem
        ...studentCoachMarksItem
        type
        userDetailsHash {
          ...userDetailsHashItem
        }
        phoneNumber
        allCourses {
          id
          academicYears {
            id
          }
          curriculumProgram {
            ...curriculumProgramBasicDetailsItem
          }
        }
        academicYears(filters: { archivalState: ACTIVE }) {
          ...academicYearItem
        }
        jwt
        isImpersonated
        linkedIdentityUsersV2 {
          totalCount
        }
        organization {
          id
          name
          tier {
            plan
            isFree
          }
          academicYears {
            id
            isCurrentAcademicYear
          }
          curriculumPrograms {
            ...curriculumProgramBasicDetailsItem #TODO: Create copy of this query for ChildrenPage and revert changes
            ...subscriptionPlanDetailsItem
          }
        }
        yearGroup {
          id
          grade {
            id
          }
        }
      }
      ... on FamilyMember {
        ...parentItem
        status
        userDetailsHash {
          ...userDetailsHashItem
        }
        phoneNumber
        communicationState {
          id
          messageType
          isSeen
          appType
        }
        children {
          ...childItem
          curriculumPrograms {
            id
            type
          }
          organization {
            id
            name
            tier {
              plan
              isFree
            }
            academicYears {
              id
              isCurrentAcademicYear
            }
            curriculumPrograms {
              ...curriculumProgramBasicDetailsItem #TODO: Create copy of this query for ChildrenPage and revert changes
              ...subscriptionPlanDetailsItem
            }
          }
          allCourses {
            id
            academicYears {
              id
            }
            curriculumProgram {
              ...curriculumProgramBasicDetailsItem
            }
          }
          curriculumProgram {
            ...curriculumProgramBasicDetailsItem
          }
          academicYears(filters: { archivalState: ACTIVE }) {
            ...academicYearItem
          }
        }
      }
    }
  }
  ${userDetailsHashFragment}
  ${curriculumProgramFragment.subscriptionPlanDetails}
  ${childFragment.basicChildDetails}
  ${parentFragment.basicParentDetails}
  ${studentFragment.basicStudentDetails}
  ${studentFragment.studentCoachMarks}
  ${staffFragment.basicStaffDetails}
  ${staffFragment.staffCoachMarks}
  ${curriculumProgramFragment.curriculumProgramBasicDetails}
  ${academicYearFragment.academicYear}
`;

export const getPlatformStaffIdentityDetailsQuery = gql`
  query getPlatformStaffIdentityDetails($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        userIdentityDetails {
          ...userIdentityDetailItem
        }
      }
    }
  }
  ${userIdentityDetailFragment.userIdentityDetail}
`;

export const getUserDetailsWithLinkedIdentitiesQuery = gql`
  query getUserDetailsWithLinkedIdentities(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $first: Int
    $after: String
    $searchText: String
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        linkedIdentityUsersV2(
          first: $first
          after: $after
          searchText: $searchText
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edgesV2 {
            node {
              ...identityUserBasicDetailsItem
            }
          }
        }
      }
      ... on Student {
        id
        linkedIdentityUsersV2(
          first: $first
          after: $after
          searchText: $searchText
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edgesV2 {
            node {
              ...identityUserBasicDetailsItem
            }
          }
        }
      }
    }
  }

  ${identityUserFragment.basicDetails}
`;

export const getOrganizationDetailsQuery = gql`
  query getOrganizationDetails($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationItem
      }
    }
  }
  ${organizationFragment.organizationDetails}
`;

export const getOrganizationPeriodSetsQuery = gql`
  query getOrganizationPeriodSets(
    $id: ID!
    $periodSetFilter: PeriodSetFilterOptions
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms {
          ...curriculumProgramItem
        }
      }
    }
  }
  ${curriculumProgramFragment.periodSetV2}
`;

export const getOrganizationPeriodSetQuery = gql`
  query getOrganizationPeriodSets(
    $id: ID!
    $periodSetFilter: PeriodSetFilterOptions
    $curriculumProgramId: [ID]
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $curriculumProgramId) {
          ...curriculumProgramItem
        }
      }
    }
  }
  ${curriculumProgramFragment.periodSet}
`;

export const getOrganizationCurriculumProgramsQuery = gql`
  query getOrganizationCurriculumPrograms($organizationId: ID!) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...curriculumProgramsItem
      }
    }
  }
  ${organizationFragment.curriculumPrograms}
`;

//added filter for academic year will move it with other planner elements in near future.

export const getPypPlannerThemeSetNodesQuery = gql`
  query getPypPlannerThemeSetNodes(
    $themeSetId: ID!
    $filters: PlannerThemeFilters
  ) {
    node(id: $themeSetId, type: PYP_THEME) {
      ... on PlannerThemeSet {
        id
        nodes(filters: $filters) {
          id
          label
        }
      }
    }
  }
`;

export const getPypPlannerElementNodesOfCurriculumQuery = gql`
  query getPypPlannerElementNodes(
    $id: ID!
    $elementTypes: [String!]
    $type: ENTITY_TYPE_ENUM!
    $grades: [ID!]
    $academicYear: ID
    $filters: PlannerEelementSetFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes, filters: $filters) {
          ...plannerElementSetWithNodesItem
        }
      }
    }
  }
  ${genericPlannerElementSetFragment.plannerElementSetWithNodes}
`;

export const getPlannerElementSetsOfCurriculumQuery = gql`
  query getPlannerElementSetsOfCurriculum(
    $id: ID!
    $elementTypes: [PLANNER_ELEMENT_TYPE_ENUM]
    $filters: PlannerElementFilters
    $type: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbMypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithNodesItem
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithNodesItem
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithNodesItem
        }
      }
    }
  }

  ${plannerElementSetFragment.plannerElementSetWithNodes}
`;

export const getYearGroupAssociatedChildPlannerElementNodesQuery = gql`
  query getYearGroupAssociatedChildPlannerElementNodesQuery(
    $id: ID!
    $filters: AssociatedChildPlannerElementNodesFilters
  ) {
    node(id: $id, type: YEAR_GROUP) {
      ... on YearGroup {
        id
        associatedChildPlannerElementNodes(filters: $filters) {
          ...plannerElementNodeItem
        }
      }
    }
  }
  ${plannerElementNodeFragment.plannerElementNode}
`;

export const getPlannerElementNodesOfSetQuery = gql`
  query getPlannerElementNodesOfSet($id: ID!, $filters: PlannerElementFilters) {
    node(id: $id, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        ...plannerElementSetWithNodesItem
      }
    }
  }
  ${plannerElementSetFragment.plannerElementSetWithNodes}
`;

export const getPlannerElementNodesOfSetWithDepthLabelQuery = gql`
  query getPlannerElementNodesOfSet(
    $id: ID!
    $filters: PlannerElementFilters
    $plannerElementDepthLabelSetFilters: PlannerElementDepthLabelSetFilters
  ) {
    node(id: $id, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        ...plannerElementSetWithNodesItem
      }
    }
  }
  ${plannerElementSetWithDepthLabelFragment.plannerElementSetWithNodes}
`;

export const getOrganizationConstantsQuery = gql`
  query getOrganizationConstant($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationItem
      }
    }
  }
  ${organizationFragment.organizationConstants}
`;

export const getCommentOfNodeQuery = gql`
  query getCommentOfNode(
    $id: ID!
    $after: String
    $type: ENTITY_TYPE_ENUM!
    $isCommunity: Boolean!
    $commentFilters: CommentsFiltersInput
  ) {
    node(id: $id, type: $type) {
      id
      ... on PlannerField {
        ...plannerFieldNodeItem
      }
      ... on Assessment {
        ...assessmentNodeItem @skip(if: $isCommunity)
        ...communityAssessmentItem @include(if: $isCommunity)
      }
      ... on LearningEngagement {
        ...leNodeItem
      }
      ... on Post {
        ...postItem
      }
      ... on GoalTask {
        ...studentGoalTaskItem
      }
      ... on UnitPlan {
        id
        ...communityUnitPlanItem @include(if: $isCommunity)
      }
      ... on EntityPlannerElementNodeMap {
        ...EntityPlannerElementNodeMap
      }
      ... on Project {
        ...projectCommentItem
      }
    }
  }
  ${commentNodeFragment.le}
  ${commentNodeFragment.assessment}
  ${commentNodeFragment.plannerField}
  ${commentNodeFragment.post}
  ${commentNodeFragment.studentGoalTask}
  ${commentNodeFragment.studentGoalTask}
  ${commentNodeFragment.communityUnitPlan}
  ${commentNodeFragment.communityAssessment}
  ${commentNodeFragment.EntityPlannerElementNodeMap}
  ${commentNodeFragment.projectComments}
`;

export const getTotalCommentOfNodeQuery = gql`
  query getTotalCommentOfNode(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $isCommunity: Boolean!
  ) {
    node(id: $id, type: $type) {
      id
      ... on PlannerField {
        ...plannerFieldNodeItem
      }
      ... on Assessment {
        ...assessmentNodeItem @skip(if: $isCommunity)
        ...communityAssessmentItem @include(if: $isCommunity)
      }
      ... on LearningEngagement {
        ...leNodeItem
      }
      ... on Post {
        ...postItem
      }
      ... on GoalTask {
        ...studentGoalTaskItem
      }
      ... on UnitPlan {
        id
        ...communityUnitPlanItem @include(if: $isCommunity)
      }
    }
  }
  ${totalCommentNodeFragment.le}
  ${totalCommentNodeFragment.assessment}
  ${totalCommentNodeFragment.plannerField}
  ${totalCommentNodeFragment.post}
  ${totalCommentNodeFragment.studentGoalTask}
  ${totalCommentNodeFragment.communityUnitPlan}
  ${totalCommentNodeFragment.communityAssessment}
`;

export const getNotificationsQuery = gql`
  query getNotifications(
    $id: ID!
    $after: String
    $type: ENTITY_TYPE_ENUM!
    $notificationCategoryFilter: [NOTIFICATION_CATEGORY_ENUM!]
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        ...staffItem
      }
      ... on Student {
        ...studentItem
      }
      ... on FamilyMember {
        ...parentItem
      }
    }
  }
  ${notificationListNodeFragment.staff}
  ${notificationListNodeFragment.student}
  ${notificationListNodeFragment.parent}
`;

export const getNotificationsCountQuery = gql`
  query getNotificationsCount(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $notificationCategoryFilter: [NOTIFICATION_CATEGORY_ENUM!]
  ) {
    node(id: $id, type: $type) {
      ... on Staff {
        ...staffCountItem
      }
      ... on Student {
        ...studentCountItem
      }
      ... on FamilyMember {
        ...parentCountItem
      }
    }
  }
  ${notificationFragment.studentCountItem}
  ${notificationFragment.staffCountItem}
  ${notificationFragment.parentCountItem}
`;

export const getPypRelatedConceptSetQuery = gql`
  query getPypRelatedConceptSet(
    $curriculumId: ID!
    $subjects: [ID!]
    $concepts: [ID!]
  ) {
    node(id: $curriculumId, type: IB_PYP_CURRICULUM_PROGRAM) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementSets(elementTypes: ["PYP_RELATED_CONCEPT"]) {
          ... on PlannerRelatedConceptSet {
            ...relatedConceptSetItem
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
  ${pypPlannerElementSetFragment.relatedConceptSet}
`;

export const getAtlSetQuery = gql`
  query getAtlSet($grades: [ID!], $organizationId: ID!) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationPypElementSetItem
      }
    }
  }

  ${organizationPypElementSetFragment.atlSet}
`;

export const getBenchmarkSetQuery = gql`
  query getBenchmarkSet(
    $subjects: [ID!]
    $grades: [ID!]
    $organizationId: ID!
    $unitPlanIds: [ID!]
    $genericTagIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationPypElementSetItem
      }
    }
  }
  ${organizationPypElementSetFragment.benchmarkSet}
`;

export const getBenchmarkSetOfCurriculumQuery = gql`
  query getBenchmarkSetOfCurriculum(
    $subjects: [ID!]
    $grades: [ID!]
    $curriculumId: ID!
    $type: ENTITY_TYPE_ENUM!
    $unitPlanIds: [ID!]
    $genericTagIds: [ID!]
  ) {
    node(id: $curriculumId, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementSets(elementTypes: ["PYP_LEARNING_STANDARD"]) {
          ... on GenericPlannerElementSet {
            ... on PlannerBenchmarkSet {
              ...PlannerBenchmarkSetItem
            }
          }
        }
      }
    }
  }
  ${pypPlannerElementSetFragment.benchmarkSet}
`;

export const getMultiLevelNodesQuery = gql`
  query getMultiLevelNodes(
    $subjectId: ID
    $organizationId: ID!
    $type: MULTI_LEVEL_NODE_TYPE!
    $parentType: NODE_DRAFT_PARENT_TYPE_ENUM
    $parentId: ID
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        getMultiLevelNodes(
          input: {
            type: $type
            subjectId: $subjectId
            parentId: $parentId
            parentType: $parentType
          }
        ) {
          rootNodes
          levels {
            id
            value
          }
          nodes {
            ...MultiLevelNodeBenchmarkItem
            ...MultiLevelNodePlannerElementItem
            ...plannerElementNodeItem
            ...benchmarkItem
          }
          levels {
            id
            value
          }
          maxDepth
          draftId
        }
      }
    }
  }
  ${nodeEditorFragment.MultiLevelNodeBenchmarkDetails}
  ${nodeEditorFragment.MultiLevelNodePlannerElementDetails}
  ${plannerElementNodeFragment.plannerElementNode}
  ${pypElementFragment.benchmarkV2}
`;

export const getPlannerTemplateQuery = gql`
  query getPlannerTemplate($ids: [ID!], $locale: String) {
    multiNode(ids: $ids, type: PLANNER_TEMPLATE) {
      id
      ... on PlannerTemplate {
        ...localisedTemplateItem
      }
    }
  }
  ${plannerTemplateFragment.localisedPlannerTemplate}
`;

export const getCurriculumAttendanceOptionsQuery = gql`
  query getCurriculumAttendanceOptionsQuery(
    $id: ID!
    $curriculumId: [ID]
    $filter: AttendanceOptionsSetFilterOptions
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        curriculumPrograms(curriculumIds: $curriculumId) {
          id
          label
          acronym
          attendanceOptionSet(filter: $filter) {
            id
            attendanceOptions {
              ...organizationAttendanceOptionItem
            }
          }
        }
      }
    }
  }
  ${organizationAttendanceOptionFragment.organizationAttendanceOption}
`;

export const getOrganizationMultiCurriculumAttendanceOptionsQuery = gql`
  query getOrganizationMultiCurriculumAttendanceOptionsQuery(
    $id: ID!
    $filter: OrganizationAttendanceFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        attendanceV2(filters: $filter) {
          attendanceOptionSet {
            id
            label
            attendanceOptions {
              curriculumPrograms {
                id
                type
                label
                acronym
              }
              attendanceOption {
                ...organizationAttendanceOptionItem
              }
            }
          }
        }
      }
    }
  }
  ${organizationAttendanceOptionFragment.organizationAttendanceOption}
`;

export const getHelpTextQuery = gql`
  query getHelpText($id: ID!, $locale: String) {
    planner {
      i18nHelpText(id: $id, locale: $locale) {
        ...helpTextItem
      }
    }
  }
  ${plannerTemplateHelpTextFragment.plannerTemplateHelpText}
`;

export const getAssessmentToolNodeDetailsQuery = gql`
  query getAssessmentToolNodeDetails($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on Rubric {
        ...rubricItem
      }
      ... on Checklist {
        ...checklist
      }
      ... on SinglePointRubric {
        ...singlePointRubricItem
      }
      ... on Exemplar {
        ...exemplarItem
      }
      ... on Anecdote {
        ...anecdoteItem
      }
      ... on MypObjectiveRubric {
        ...mypObjectiveRubricItem
      }
      ... on StandardRubric {
        ...standardRubricItem
      }
      ... on GradeScaleEntityDetail {
        ...gradeScaleEntityDetailsItem
      }
    }
  }
  ${rubricFragment.rubric}
  ${checklistFragment.checklist}
  ${singlePointRubricFragment.singlePointRubric}
  ${assessmentToolFragment.mypObjectiveRubric}
  ${assessmentToolFragment.exemplar}
  ${assessmentToolFragment.anecdote}
  ${standardRubricFragment.standardRubric}
  ${gradeScaleToolFragment.gradeScaleEntityDetails}
`;

export const getAssessmentToolsDetailsByStudentAssignmentIdQuery = gql`
  # Query to fetch all assessment tools details ( used in new theatre mode )
  query getAssessmentToolsDetailsByStudentAssignmentId($id: ID!) {
    node(id: $id, type: STUDENT_ASSIGNMENT) {
      id
      ... on StudentAssignment {
        id
        assignment {
          id
          content {
            id
            ... on Assessment {
              id
              assessmentTools {
                totalCount
                edges {
                  node {
                    id
                    assessmentToolType
                    isStudentEvaluationEnable
                    isTeacherEvaluationEnable
                    assessmentToolItem {
                      id
                      ... on Rubric {
                        ...rubricItem
                      }
                      ... on Checklist {
                        ...checklist
                      }
                      ... on SinglePointRubric {
                        ...singlePointRubricItem
                      }
                      ... on Exemplar {
                        ...exemplarItem
                      }
                      ... on Anecdote {
                        ...anecdoteItem
                      }
                      ... on MypObjectiveRubric {
                        ...mypObjectiveRubricItem
                      }
                      ... on AcademicCriteriaSet {
                        id
                        academicCriteriaValues {
                          id
                          label
                        }
                        criteriaType
                      }
                      ... on StandardRubric {
                        ...standardRubricItem
                      }
                      ... on GradeScaleEntityDetail {
                        ...gradeScaleEntityDetailsItem
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${rubricFragment.rubric}
  ${checklistFragment.checklist}
  ${singlePointRubricFragment.singlePointRubric}
  ${assessmentToolFragment.mypObjectiveRubric}
  ${assessmentToolFragment.exemplar}
  ${assessmentToolFragment.anecdote}
  ${standardRubricFragment.standardRubric}
  ${gradeScaleToolFragment.gradeScaleEntityDetails}
`;

export const getAssessmentToolsDetailsByPostIdQuery = gql`
  # Query to fetch all assessment tools details in PUBLIC URL ( used in new theatre mode )
  query getAssessmentToolsDetailsByPostId($id: ID!) {
    node(id: $id, type: POST) {
      id
      ... on Post {
        id
        items {
          item {
            ... on StudentAssignment {
              id
              assignment {
                id
                content {
                  id
                  ... on Assessment {
                    id
                    assessmentTools {
                      totalCount
                      edges {
                        node {
                          id
                          assessmentToolType
                          isStudentEvaluationEnable
                          isTeacherEvaluationEnable
                          assessmentToolItem {
                            id
                            ... on Rubric {
                              ...rubricItem
                            }
                            ... on Checklist {
                              ...checklist
                            }
                            ... on SinglePointRubric {
                              ...singlePointRubricItem
                            }
                            ... on Exemplar {
                              ...exemplarItem
                            }
                            ... on Anecdote {
                              ...anecdoteItem
                            }
                            ... on MypObjectiveRubric {
                              ...mypObjectiveRubricItem
                            }
                            ... on AcademicCriteriaSet {
                              id
                              academicCriteriaValues {
                                id
                                label
                              }
                              criteriaType
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${rubricFragment.rubric}
  ${checklistFragment.checklist}
  ${singlePointRubricFragment.singlePointRubric}
  ${assessmentToolFragment.mypObjectiveRubric}
  ${assessmentToolFragment.exemplar}
  ${assessmentToolFragment.anecdote}
`;

export const getSubjectBenchmarksQuery = gql`
  query getSubjectBenchmarks($subject: ID!, $grades: [ID!]) {
    node(id: $subject, type: SUBJECT) {
      id
      ... on Subject {
        id
        isDemo
        benchmarks(grades: $grades) {
          ...benchmarkItem
        }
      }
    }
  }
  ${pypElementFragment.benchmark}
`;

export const getATLsQuery = gql`
  query getALTs($id: ID!, $grades: [ID!]) {
    node(id: $id, type: ATL_CATEGORY) {
      id
      ... on ATLCategory {
        id
        atls(grades: $grades) {
          ...atlItem
        }
      }
    }
  }
  ${pypElementFragment.atl}
`;

export const getUserZoomAuthorizedStatusQuery = gql`
  query getUserZoomAuthorizedStatus($id: ID!) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        isZoomAuthorized
      }
    }
  }
`;

export const getResourceQuery = gql`
  query getResource($id: ID!) {
    node(id: $id, type: PLANNER_RESOURCE) {
      id
      ... on PlannerResource {
        id
        title
        description
        attachments {
          ...attachmentItem
        }
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const getUserThirdPartyAuthorizedStatusQuery = gql`
  query getUserThirdPartyAuthorizedStatus(
    $id: ID!
    $serviceType: THIRD_PARTY_SERVICE_TYPE
    $type: ENTITY_TYPE_ENUM!
    $skipAccessToken: Boolean!
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        linkedThirdPartyAccounts(serviceType: $serviceType) {
          ...thirdPartyAccountItem
        }
      }
      ... on Student {
        id
        linkedThirdPartyAccounts(serviceType: $serviceType) {
          ...thirdPartyAccountItem
        }
      }
      ... on FamilyMember {
        id
        linkedThirdPartyAccounts(serviceType: $serviceType) {
          ...thirdPartyAccountItem
        }
      }
    }
  }
  ${thirdPartyAccountFragment.thirdPartyAccount}
`;

export const getUserThirdPartyPickerTokensQuery = gql`
  query getUserThirdPartyPickerTokens(
    $id: ID!
    $serviceType: THIRD_PARTY_SERVICE_TYPE
    $type: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        linkedThirdPartyAccountFilePickerTokens(serviceType: $serviceType) {
          serviceType
          data
        }
      }
      ... on Student {
        id
        linkedThirdPartyAccountFilePickerTokens(serviceType: $serviceType) {
          serviceType
          data
        }
      }
      ... on FamilyMember {
        id
        linkedThirdPartyAccountFilePickerTokens(serviceType: $serviceType) {
          serviceType
          data
        }
      }
    }
  }
`;

export const getSubjectsByGradeQuery = gql`
  query getSubjectsByGrade($id: ID!) {
    node(id: $id, type: GRADE) {
      id
      ... on Grade {
        id
        name
        subjects(filters: { archivalState: ALL }) {
          id
          name
          icon
          type
          isArchived
          ibOfferedSubject {
            id
            name
            contentSubject {
              id
              name
              assessmentYearTitle
            }
          }
          variants {
            level {
              id
              label
            }
          }
          flatVariants {
            isArchived
            level {
              id
              label
            }
          }
          subjectGroup {
            id
            name
            type
          }
        }
      }
    }
  }
`;

export const getDpSubjectVariantsQuery = gql`
  query getOrganizationSubjectVariants($id: ID!) {
    node(id: $id, type: IB_DP_CURRICULUM_PROGRAM) {
      ... on IbDpCurriculumProgram {
        id
        subjectVariants(filters: { archivalState: ALL }) {
          subject {
            name
            id
            icon
            ibOfferedSubject {
              id
              contentSubject {
                id
                assessmentYearTitle
              }
            }
            isArchived
          }
          subjectGroup {
            id
            name
            type
            globalSubjectGroup {
              id
              name
              type
            }
          }
          level {
            id
            label
            globalTag {
              id
            }
          }
          responseLanguages {
            id
            label
            globalTag {
              id
            }
          }
        }
      }
    }
  }
`;

export const getDpSubjectVariantsQueryV2 = gql`
  query getOrganizationSubjectVariants($id: ID!) {
    node(id: $id, type: IB_DP_CURRICULUM_PROGRAM) {
      ... on IbDpCurriculumProgram {
        id
        subjectVariants(filters: { archivalState: ALL }) {
          subject {
            name
            id
            icon
            ibOfferedSubject {
              id
              contentSubject {
                id
                assessmentYearTitle
                assessmentYearEndsOn
              }
            }
            isArchived
            flatVariants(filters: { archivalState: ALL }) {
              level {
                id
                label
              }
              responseLanguage {
                id
                label
              }
              isArchived
            }
          }
          subjectGroup {
            id
            name
            type
            globalSubjectGroup {
              id
              name
              type
            }
            displaySequence
          }
        }
      }
    }
  }
`;

export const getIbOfferedSubjectVariants = gql`
  query getOrganizationGlobalSubjectVariants(
    $subjectVariantFilters: IbOfferedSubjectVariantFilters
    $subjectGroupFilters: GlobalSubjectGroupFilters
    $globalTagFilters: GlobalGenericTagFilters
  ) {
    globalConstants {
      ibOfferedSubjectVariants(filters: $subjectVariantFilters) {
        subject {
          id
          name
          contentSubject {
            id
            assessmentYearTitle
          }
        }
        subjectGroup {
          id
          name
          type
          displaySequence
        }
        level {
          id
          label
        }
        responseLanguages {
          id
          label
        }
      }
      globalSubjectGroups(filters: $subjectGroupFilters) {
        id
        name
        type
        displaySequence
      }
      globalGenericTags(filters: $globalTagFilters) {
        id
        label
        type
      }
    }
  }
`;

export const getOrganizationGlobalGenericTagsQuery = gql`
  query getOrganizationGlobalGenericTags(
    $globalTagFilters: GlobalGenericTagFilters
  ) {
    globalConstants {
      globalGenericTags(filters: $globalTagFilters) {
        id
        label
        type
      }
    }
  }
`;

export const getAllOrganizationFeedQuery = gql`
  query getAllOrganizationFeed($filters: AllOrganizationFeedConnectionFilters) {
    platform {
      allOrganizationFeed(filters: $filters) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            name
            logo
            region
          }
          cursor
        }
      }
    }
  }
`;

export const getOrganizationSubjectGroupsQuery = gql`
  query getOrganizationSubjectGroups(
    $id: ID!
    $filters: SubjectGroupFilterInput
    $subjectFilters: SubjectFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationItem
      }
    }
  }
  ${organizationFragment.organizationSubjectGroups}
`;

export const getOrganizationSubjectsQuery = gql`
  query getOrganizationSubjects($id: ID!, $filters: SubjectFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationItem
      }
    }
  }
  ${organizationFragment.organizationSubjects}
`;

export const getOrganizationSubjectsQueryV2 = gql`
  query getOrganizationSubjects(
    $id: ID!
    $filters: SubjectFilterInput
    $plannerNodeFilters: AssociatedChildPlannerElementNodesFilters
    $flatSubjectVariantFilters: FlatSubjectVariantFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationItem
      }
    }
  }
  ${organizationFragment.organizationSubjectsV2}
`;

export const getAcademicCriteriaSetsQuery = gql`
  query getAcademicCriteriaSets(
    $organizationId: ID!
    $filters: academicCriteriaSetsFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        constants {
          academicCriteriaSets(filters: $filters) {
            ...criteriaSetItem
          }
        }
      }
    }
  }
  ${criteriaSetFragment.criteriaSet}
`;

export const getOrganizationElementRatingQuery = gql`
  query getOrganizationElementRating(
    $organizationId: ID!
    $filters: RatingLevelsFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        constants {
          ratingLevels(filters: $filters) {
            ...ratingItem
          }
        }
      }
    }
  }
  ${pypElementRatingFragment.organizationElementRating}
`;

export const getChildrenQuery = gql`
  query getChildren(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $organizationId: ID
    $teacherId: ID
    $includeInvitedStudents: Boolean
  ) {
    node(id: $id, type: $type) {
      id
      ... on FamilyMember {
        children(
          organizationId: $organizationId
          teacherId: $teacherId
          includeInvitedStudents: $includeInvitedStudents
        ) {
          ...childItem
          ...childDetailsItem
          academicYears(filters: { archivalState: ACTIVE }) {
            ...academicYearItem
          }
        }
      }
    }
  }
  ${childFragment.basicChildDetails}
  ${childFragment.childDetails}
  ${academicYearFragment.academicYear}
`;

export const getPypAtlConstantsQuery = gql`
  query getPypAtlConstants($pypAtlSetId: ID!, $filters: ATLFilter) {
    node(id: $pypAtlSetId, type: PYP_ATL) {
      ... on ATLSet {
        id
        rootNodes
        nodes(filters: $filters) {
          ...atlItem
        }
      }
    }
  }
  ${pypElementFragment.atl}
`;

export const getOrganizationGradesQuery = gql`
  query getOrganizationGrades($id: ID!, $filters: GradeFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $filters) {
          id
          name
          displaySequence
          yearGroup {
            id
            examSession {
              id
              year
            }
          }
          curriculumProgram {
            id
            type
            acronym
          }
          globalGrade {
            id
            uid
            name
          }
          subjects {
            id
          }
        }
      }
    }
  }
`;

export const getStaffListQuery = gql`
  query getStaffList(
    $organizationId: ID!
    $searchText: String
    $grades: [ID!]
    $courses: [ID!]
    $archivalState: ARCHIVAL_STATE_ENUM
    $curriculumProgramIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(
          filters: {
            searchText: $searchText
            grades: $grades
            courses: $courses
            archivalState: $archivalState
            curriculumProgramIds: $curriculumProgramIds
          }
        ) {
          totalCount
          edges {
            node {
              ...staffCoursesItem
            }
          }
        }
      }
    }
  }

  ${staffUnitPlanFragment.staffCourses}
`;

export const getOrganizationStaffQuery = gql`
  query getOrganizationStaff(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(filters: $filters) {
          totalCount
          edges {
            node {
              ...userItem
            }
          }
        }
      }
    }
  }
  ${userFragment.basicUser}
`;

export const getUserGradesQuery = gql`
  query getUserGrades(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $allGradesFilters: AllGradesFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on Student {
        id
        allGrades(filters: $allGradesFilters) {
          ...gradeItem
        }
      }
      ... on Staff {
        id
        allGrades(filters: $allGradesFilters) {
          ...gradeItem
        }
      }
    }
  }
  ${gradeFragment.basicGradeDetails}
`;

export const getFilteredPostCountWithTypeQuery = gql`
  query getFilteredPostCount(
    $id: ID!
    $filters: PostFilterInput
    $type: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $type) {
      ... on Student {
        id
        posts(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getOrganizationGlobalConstantsQuery = gql`
  query getOrganizationGlobalConstants(
    $organizationId: ID!
    $filter: GlobalConstantsFilterOptions
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        globalConstants(filter: $filter) {
          ...organizationGlobalConstantItem
        }
      }
    }
  }
  ${organizationFragment.organizationGlobalConstants}
`;

export const getTurnitinReportViewerLaunchUrlQuery = gql`
  query getTurnitinReportViewerLaunchUrl(
    $input: TurnitinReportViewerLauncherUrlInput!
  ) {
    integration {
      getTurnitinReportViewerLaunchUrl(input: $input)
    }
  }
`;

export const getIsTurnitinEulaRequiredQuery = gql`
  query getIsTurnitinEulaRequired {
    integration {
      isTurnitinEulaRequired
    }
  }
`;

export const getLatestTurnitinEulaQuery = gql`
  query getLatestTurnitinEula($input: GetLatestTurnitinEulaInput) {
    integration {
      getLatestTurnitinEula(input: $input) {
        version
        content
        locale
      }
    }
  }
`;

export const getOrganizationSubjectsWithPlannerElementDepthLabelSetsQuery = gql`
  query getOrganizationSubjectsWithPlannerElementDepthLabelSets(
    $id: ID!
    $subjectFilters: SubjectFilterInput
    $plannerDepthFilters: PlannerElementDepthLabelSetFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        subjects(filters: $subjectFilters) {
          id
          name
          isArchived
          plannerElementDepthLabelSets(filters: $plannerDepthFilters) {
            plannerElementSet {
              id
            }
            plannerElementDepthLabels {
              id
              depth
              label
            }
          }
        }
      }
    }
  }
`;

export const getPlannerElementSetNodesAndDepthLabelsQuery = gql`
  query getPlannerElementSetNodesWithDepthLabelSets(
    $id: ID!
    $filters: PlannerElementFilters
  ) {
    node(id: $id, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        nodes(filters: $filters) {
          id
          label
          depth
        }
        depthLabels {
          id
          depth
          label
        }
      }
    }
  }
`;

export const getCourseUnitPlanCountOfSubjectQuery = gql`
  query getCourseUnitPlanCountOfSubject($id: ID!) {
    node(id: $id, type: SUBJECT) {
      ... on Subject {
        id
        courseCount
        unitPlanCount
      }
    }
  }
`;

export const getFilteredTags = gql`
  query getFilteredTags($keys: [String!]!) {
    platform {
      allTags(input: { keys: $keys }) {
        id
        key
        value
      }
    }
  }
`;

export const getStudentPeriodListQuery = gql`
  query getStudentPeriodList($id: ID!, $periodFilters: PeriodFilterOptions) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        ...studentPeriodListItem
      }
    }
  }
  ${studentFragment.studentPeriodList}
`;

export const getUserPeriodListQuery = gql`
  query getUserPeriodList(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $periodFilters: PeriodFilterOptions
  ) {
    node(id: $id, type: $type) {
      ... on Student {
        ...studentPeriodListItem
      }
      ... on Staff {
        ...staffPeriodListItem
      }
    }
  }
  ${studentFragment.studentPeriodList}
  ${staffPeriodListFragment.staffPeriodList}
`;

export const getStudentPeriodListInfoQuery = gql`
  query getStudentPeriodListInfo(
    $id: ID!
    $periodFilters: PeriodFilterOptions
  ) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        ...studentPeriodListInfoItem
      }
    }
  }
  ${studentFragment.studentPeriodListInfo}
`;

export const getOrganizationYearGroupIdsQuery = gql`
  query getOrganizationYearGroups($id: ID!, $filters: YearGroupFilter) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        yearGroups(filters: $filters) {
          id
        }
      }
    }
  }
`;

export const getOrganizationYearGroupsQuery = gql`
  query getOrganizationYearGroups($id: ID!, $filters: YearGroupFilter) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        yearGroups(filters: $filters) {
          ...basicYearGroupDetails
        }
      }
    }
  }
  ${yearGroupFragments.basicYearGroupDetails}
`;

export const getOrganizationYearGroupsNameQuery = gql`
  query getOrganizationYearGroups($id: ID!, $filters: YearGroupFilter) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        yearGroups(filters: $filters) {
          id
          name
          isArchived
        }
      }
    }
  }
`;

export const getOrganizationExamSessionsQuery = gql`
  query getOrganizationExamSession($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        examSessions {
          id
          session
          month
        }
      }
    }
  }
`;

export const getOrganizationMultiLevelNodeTagsQuery = gql`
  query getOrganizationMultiLevelNodeTags(
    $organizationId: ID!
    $filters: GenericTagsFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        name
        genericTagsV2(filters: $filters) {
          ...genericTagDetailsItem
        }
      }
    }
  }
  ${genericTagFragment.genericTagDetails}
`;

export const getOrganizationTemplatesQuery = gql`
  query getOrganizationTemplates(
    $id: ID!
    $filters: OrganizationTemplateFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        templates(filters: $filters) {
          id
          body
        }
      }
    }
  }
`;

export const getStudentStaffRelationQuery = gql`
  query student(
    $id: ID!
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $filters: StaffStudentRelationFilter
  ) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        ...studentItem
        staffRelation(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          totalCount
          edges {
            node {
              ... on Staff {
                ...staffItem
              }
              ... on Student {
                ...studentItem
              }
            }
            cursor
          }
          pageInfo {
            ...pageInfoItem
          }
        }
      }
    }
  }
  ${pageInfoFragment.pageInfo}
  ${studentFragment.basicStudentDetails}
  ${staffFragment.basicStaffDetails}
`;

export const getOrganizationTemplatesWithHelpEnabledFieldsQuery = gql`
  query getOrganizationTemplates(
    $id: ID!
    $filters: OrganizationTemplateFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        templates(filters: $filters) {
          id
          body
          helpEnabledFields {
            id
            uid
            helpText {
              id
            }
            type
          }
        }
      }
    }
  }
`;

export const getOrganizationSubjectsBasicDetailsQuery = gql`
  query getOrganizationSubjectsAssociations(
    $id: ID!
    $filters: SubjectFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationItem
      }
    }
  }
  ${organizationFragment.organizationSubjectsBasicDetails}
`;

export const getTemplateFieldPropertiesQuery = gql`
  query getTemplateFieldProperties(
    $templateFieldId: ID!
    $configFilter: TemplateFieldConfigFilter
    $valueFilter: TemplateFieldPropertyValuesFilter
  ) {
    node(id: $templateFieldId, type: TEMPLATE_FIELD) {
      id
      ... on TemplateField {
        id
        config(filter: $configFilter) {
          id
          globalFieldProperty {
            id
            property
          }
          values(filters: $valueFilter) {
            id
            value
            configLevel {
              id
              type
            }
          }
        }
      }
    }
  }
`;

export const getAIConversationsQuery = gql`
  query getAiConverstion($id: ID!) {
    node(id: $id, type: AI_CONVERSATION) {
      ... on AiConversation {
        id
        entityType
        metadata
        messages {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...aiMessageItem
            }
          }
        }
      }
    }
  }
  ${aiFragment.aiMessage}
`;

export const getAIConversationsQueryWithPublicUrl = gql`
  query getAiConverstion($id: ID!) {
    node(id: $id, type: AI_CONVERSATION) {
      ... on AiConversation {
        id
        entityType
        metadata
        messages {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...aiMessageItem
            }
          }
        }
        attachments {
          ...attachmentItem
        }
        publicUrl
      }
    }
  }
  ${attachmentFragment.attachment}
  ${AIMessageFragments.AIMessage}
`;

export const getAiConversationsQuery = gql`
  query getAiConverstions($filters: AiConversationFilters) {
    ai {
      aiConversations(filters: $filters) {
        edges {
          node {
            id
            entityType
            metadata
          }
        }
      }
    }
  }
`;

export const getAIPromptsQuery = gql`
  query getAIPrompts($filters: PromptFilters!) {
    ai {
      prompts(filters: $filters) {
        id
        displayText
        displayTextLocale
        entityType
        promptType
        action
        fieldType
        displaySequence
        groupType
        isTimeDependent
        startDate
        endDate
        type
        promptFields {
          field
          type
          config
          isRequired
        }
        children
        metadata
      }
    }
  }
`;

export const getAIPromptSetsQuery = gql`
  query getAIPromptSets($filters: PromptSetFilters!) {
    ai {
      promptSets(filters: $filters) {
        id
        label
        entityType
      }
    }
  }
`;

export const getUbdAssessmentTypesQuery = gql`
  query getUbdAssessmentTypes($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on UBDCurriculumProgram {
        id
        assessmentTypes {
          id
          uid
          label
          metadata
        }
      }
    }
  }
`;

export const verifySentralOauthRedirectQuery = gql`
  query verifySentralOauthRedirect($input: VerifySentralOauthRedirect!) {
    integration {
      verifySentralOauthRedirect(input: $input) {
        isSuccess
        redirectUrl
        message
      }
    }
  }
`;

export const fetchSentralUserNotificationStateQuery = gql`
  query fetchSentralUserNotificationState(
    $input: FetchSentralUserNotificationStateInput!
  ) {
    integration {
      fetchSentralUserNotificationState(input: $input)
    }
  }
`;

export const getUserRnpRolesQuery = gql`
  query getUserRnpRoles(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: RnPRoleContextDetailFilterInput
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        rnpRoleContextDetail(filters: $filters) {
          id
          contextType
          role {
            id
            isDefaultRole
            globalRole {
              id
              uid
            }
          }
        }
      }
    }
  }
`;

export const getAssessmentGradesQuery = gql`
  query getOrganizationGrades($assessmentId: ID!) {
    node(id: $assessmentId, type: ASSESSMENT) {
      id
      ... on Assessment {
        id
        grades {
          id
          name
          globalGrade {
            id
            uid
          }
        }
      }
    }
  }
`;
